import {Injectable, inject} from '@angular/core';
import {IUser} from '@evencel/models';
import {Action, Selector, State, StateContext} from '@ngxs/store';
import {mergeMap, tap} from 'rxjs';
import {UserService} from '../../services';
import * as actions from './user.actions';

export interface UserStateModel {
    user?: IUser;
}

@State<UserStateModel>({name: 'user'})
@Injectable({providedIn: 'root'})
export class UserState {
    @Selector() static securityClearance(state: UserStateModel) {
        return state.user?.securityClearance || [];
    }

    userService = inject(UserService);

    @Action(actions.CreateUser)
    create({dispatch}: StateContext<void>, {user}: actions.CreateUser) {
        return this.userService.post(user).pipe(mergeMap(data => dispatch(new actions.GetUserResponse(data))));
    }

    @Action(actions.UpdateUser)
    update({dispatch}: StateContext<void>, {user}: actions.UpdateUser) {
        return this.userService.patch(user).pipe(mergeMap(data => dispatch(new actions.GetUserResponse(data))));
    }

    @Action(actions.ActivateUser)
    activate({dispatch}: StateContext<void>, {user}: actions.ActivateUser) {
        return this.userService.activate(user).pipe(tap(data => dispatch(new actions.GetUserResponse(data))));
    }

    @Action(actions.DeleteUser)
    delete(context: StateContext<void>, {user}: actions.DeleteUser) {
        return this.userService.delete(user._id);
    }

    @Action(actions.RestoreUser)
    restore(context: StateContext<void>, {user}: actions.RestoreUser) {
        return this.userService.restore(user._id);
    }

    @Action(actions.AddSecurityClearence)
    addSecurityClearence(cotext: StateContext<void>, {data}: actions.AddSecurityClearence) {
        return this.userService.addSecurityClearence(data);
    }

    @Action(actions.GetSecurityClearence)
    getSecurityClearence({patchState}: StateContext<UserStateModel>) {
        return this.userService.getSecurityClearence().pipe(tap(user => patchState({user})));
    }

    @Action(actions.UpsertContinuingEducation)
    upsertContinuingEducation({dispatch}: StateContext<void>, {education}: actions.UpsertContinuingEducation) {
        if (education._id) {
            return this.userService
                .updateContinuingEducation(education)
                .pipe(tap(data => dispatch(new actions.GetUserResponse(data))));
        } else {
            return this.userService
                .addContinuingEducation(education)
                .pipe(tap(data => dispatch(new actions.GetUserResponse(data))));
        }
    }

    @Action(actions.DeleteContinuingEducation)
    deleteContinuingEducation({dispatch}: StateContext<void>, {education}: actions.UpsertContinuingEducation) {
        return this.userService
            .deleteContinuingEducation(education)
            .pipe(tap(data => dispatch(new actions.GetUserResponse(data))));
    }

    @Action(actions.GetUser)
    getOne({dispatch}: StateContext<void>, {id, projection}: actions.GetUser) {
        return this.userService.getOne(id, projection).pipe(tap(data => dispatch(new actions.GetUserResponse(data))));
    }

    @Action(actions.SearchUsers)
    get({dispatch}: StateContext<void>, {data}: actions.SearchUsers) {
        let obs = null;
        if (data.condition) obs = this.userService.search(data);
        else obs = this.userService.get(data);

        return obs.pipe(tap(data => dispatch(new actions.GetUsersResponse(data, {state: 'success'}))));
    }

    @Action(actions.GetUserGeneral)
    getUserGeneral({dispatch}: StateContext<void>, {id}: actions.GetUserGeneral) {
        return this.userService
            .getUserGeneralInfo(id)
            .pipe(tap(data => dispatch(new actions.GetUserGeneralResponse(data))));
    }
}
